import { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import CSSModules from 'react-css-modules';
import { useDebouncedCallback } from 'use-debounce';
import { Icon, FieldCustomNode, TooltipTip } from 'platfi-ui-components';
import { asField, withFormApi } from 'informed';

import { shareableReportInputState } from 'enums/shareable-report-input-state';
import { AnalyticsEventAction, AnalyticsEventCategory } from 'enums/analytics-types';

import { Analytics } from 'utils/Analytics';

import ShareableReportTitledSpinner from '../ShareableReportTitledSpinner/ShareableReportTitledSpinner';

import styles from './ShareableReportTextArea.scss';

const CustomNodeAsField = asField(FieldCustomNode);

const { success, loading, idle } = shareableReportInputState;

const tips = [
  'Provide a summary of the lenders that have been approached.',
  'Explain why some (if any) lenders have declined to offer.',
  'Set expectations for the next step of the process.',
];

const ShareableReportTextArea = (props) => {
  const {
    fieldName,
    title,
    minRows,
    className,
    placeholderClassName,
    onPlaceholderClick,
    formApi,
  } = props;

  const [isActive, setIsActive] = useState(false);
  const [rows, setRows] = useState(minRows);
  const [inputState, setInputState] = useState(idle);
  const input = useRef(null);
  const formState = formApi.getState();
  const inputValue = formState?.values[fieldName];

  const handleRowsExpand = () => {
    if (!input?.current) {
      return;
    }

    const textareaLineHeight = 19;
    const rowsForSpinner = 2;

    const previousRows = input.current.rows;
    input.current.rows = minRows - rowsForSpinner;

    const currentRows = Math.ceil(input.current.scrollHeight / textareaLineHeight);

    if (currentRows === previousRows) {
      input.current.rows = currentRows;
    }

    setRows(currentRows);
  };

  const handleSave = () => {
    // simulate API
    if (inputValue && inputState !== loading) {
      setInputState(loading);

      setTimeout(() => {
        setInputState(success);
        setTimeout(() => {
          setInputState(idle);
        }, 2000);
      }, 2000);
    }

    Analytics.track(AnalyticsEventCategory.responses, AnalyticsEventAction.offerReportUpdated);
  };

  const stateHandler = useDebouncedCallback(handleSave, 1500);

  useEffect(stateHandler, [inputValue]);

  return (
    <div styleName="m-shareable-report-text-area" className={className}>
      {inputValue || isActive ? (
        <div>
          <div styleName="m-shareable-report-text-area__container">
            <div styleName="m-shareable-report-text-area__input-container">
              <CustomNodeAsField
                field={fieldName}
                onChange={handleRowsExpand}
                onBlur={() => {
                  setIsActive(false);
                  handleSave();
                }}
                onFocus={() => !isActive && setIsActive(true)}
                styleName="m-shareable-report-text-area__input"
                forwardedRef={input}
                autoFocus
                rows={rows}
              >
                <textarea />
              </CustomNodeAsField>
            </div>

            {inputValue && (
              <div styleName="m-shareable-report-text-area__tooltip">
                <TooltipTip hideAction warn>
                  {tips.map((tip) => tip)}
                </TooltipTip>
              </div>
            )}
          </div>

          {!isActive && (
            <Icon
              className="a-svg-small"
              icon="pencil"
              styleName="m-shareable-report-text-area__input-icon"
            />
          )}

          <ShareableReportTitledSpinner
            styleName="m-shareable-report-text-area__input-spinner"
            state={inputState}
          />
        </div>
      ) : (
        <div
          styleName="m-shareable-report-text-area__placeholder"
          className={placeholderClassName}
          onClick={() => {
            setIsActive(true);
            onPlaceholderClick && onPlaceholderClick();
          }}
          role="button"
          onKeyPress={() => setIsActive(true)}
          tabIndex={0}
        >
          <Icon
            className="a-svg-large"
            icon="message"
            styleName="m-shareable-report-text-area__placeholder__icon"
          />
          {title && <div styleName="m-shareable-report-text-area__placeholder__title">{title}</div>}
        </div>
      )}
    </div>
  );
};

ShareableReportTextArea.propTypes = {
  fieldName: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  minRows: PropTypes.number.isRequired,
  className: PropTypes.string,
  placeholderClassName: PropTypes.string,
  formApi: PropTypes.object.isRequired,
  onPlaceholderClick: PropTypes.func,
};

export default withFormApi(
  CSSModules(ShareableReportTextArea, styles, {
    allowMultiple: true,
  }),
);
